/**
 * The one audio context, and the gesture that unlocks it.
 *
 * This used to live inside the scanner, because the scanner was the only thing
 * in the app that made a sound. It moved out when she started making them too,
 * and the reason it had to move is the same reason it was written the way it
 * was: WebKit gives a page exactly one chance to unlock audio — the first user
 * gesture — and a second context built later is born suspended and stays that
 * way. Two owners, one context.
 *
 * Android vibrates; an iPhone cannot, since WebKit ships no Vibration API. The
 * scanner keeps its vibration because a scan wants every channel the platform
 * allows. Her sounds add none: haptics that only fire on the phone neither
 * resident carries would be a feature written for nobody.
 *
 * Sound still loses to the ring/silent switch on iOS, which is why nothing in
 * this app is ever announced by sound alone.
 */

type AudioCtor = typeof AudioContext

let context: AudioContext | null = null

function audioConstructor(): AudioCtor | null {
  const w = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor }
  return w.AudioContext ?? w.webkitAudioContext ?? null
}

/**
 * Build the context and unlock it.
 *
 * Only does anything useful from inside a user gesture: everywhere else
 * `resume()` resolves with the context still suspended. Safe to call again on
 * every tap, and cheap enough that doing so is the simplest way to be sure.
 */
export function primeAudio(): void {
  const Ctor = audioConstructor()
  if (!Ctor) return
  try {
    context ??= new Ctor()
    void context.resume().catch(() => undefined)
    // iOS counts a context as unlocked only once something has been played
    // through it, so the gesture buys a silent hundredth of a second.
    const silent = context.createOscillator()
    const gain = context.createGain()
    gain.gain.value = 0
    silent.connect(gain).connect(context.destination)
    silent.start()
    silent.stop(context.currentTime + 0.01)
  } catch {
    context = null
  }
}

/**
 * The context, or null if it does not exist or has never been unlocked.
 *
 * Callers are expected to treat null as "no sound this time" rather than as an
 * error worth handling: before the first tap of a session there is genuinely
 * nothing that can be done, and a cue that queues itself up to fire late is
 * worse than one that never fires.
 */
export function audioContext(): AudioContext | null {
  const ctx = context
  if (ctx == null) return null
  if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined)
  return ctx
}

/**
 * Unlock on the first gesture of the session, wherever it lands.
 *
 * Registered by the app rather than by any one feature: reaching the scanner
 * takes a tap that has already finished by the time it mounts, so a listener
 * added there would sit out the first scan.
 */
export function unlockAudioOnGesture(): () => void {
  const prime = () => primeAudio()
  const options = { capture: true, once: true, passive: true } as const
  document.addEventListener('pointerdown', prime, options)
  document.addEventListener('touchend', prime, options)
  return () => {
    document.removeEventListener('pointerdown', prime, { capture: true })
    document.removeEventListener('touchend', prime, { capture: true })
  }
}
